import React, { useState, useEffect } from "react";
import SEO from "./SEO";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
// import dashboardPreview from "../assets/images/boxia-dashboard-preview.png";
import googleLogo from "../assets/images/logos/google.svg";
import microsoftLogo from "../assets/images/logos/microsoft.svg";
import jiraLogo from "../assets/images/logos/jira.svg";
import trelloLogo from "../assets/images/logos/trello.svg";
import slackLogo from "../assets/images/logos/slack.svg";
import githubLogo from "../assets/images/logos/github.svg";
import dropboxLogo from "../assets/images/logos/dropbox.svg";
import salesforceLogo from "../assets/images/logos/salesforce.svg";
import asanaLogo from "../assets/images/logos/asana.svg";
import evernoteLogo from "../assets/images/logos/evernote.svg";
import zapierLogo from "../assets/images/logos/zapier.svg";
import hubspotLogo from "../assets/images/logos/hubspot.svg";
import openaiLogo from "../assets/images/logos/openai-lockup.svg";
import microsoftBig from "../assets/images/logos/Microsoft_logo_(2012).svg";
import amazonLogo from "../assets/images/logos/Amazon_logo.svg";
import metaLogo from "../assets/images/logos/Meta.svg";
import googleBig from "../assets/images/logos/googlebig.svg";
import githubBig from "../assets/images/logos/githubbig.svg";

import BoxiaHome from "../assets/images/Boxia-Platform-Nov.png";
import { HeroSection } from "./blocks/hero-section-dark";
import {
  ChevronRight,
  Calendar,
  FileText,
  Users,
  Pencil,
  FolderOpen,
  MessageSquare,
  Bot,
  Shield,
  Cog,
  FileEdit,
  Brain,
  ShieldCheck,
  CalendarDays,
  FolderGit2,
  MessagesSquare,
  Workflow,
  KeyRound,
  Code2,
  Palette,
  LayoutDashboard,
  KanbanSquare,
  Bell,
  Settings2,
  Lock,
  Key,
  Fingerprint,
  History,
  ServerCrash,
  ChevronDown,
  ArrowRight,
  Zap,
  Sparkles,
  SparkleIcon,
  Link2,
  SlidersHorizontal,
} from "lucide-react";

const LandingPage = () => {
  const [darkMode] = useState(false); // Default to light mode
  const [logoFade] = useState(false);
  const [annualBilling, setAnnualBilling] = useState(true);
  const [teamUsers, setTeamUsers] = useState(3);
  const [openStates, setOpenStates] = useState([]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const integrations = [
    { name: "Google", logo: googleLogo },
    { name: "Microsoft", logo: microsoftLogo },
    { name: "Jira", logo: jiraLogo },
    { name: "Trello", logo: trelloLogo },
    { name: "Slack", logo: slackLogo },
    { name: "GitHub", logo: githubLogo },
    { name: "Dropbox", logo: dropboxLogo },
    { name: "Salesforce", logo: salesforceLogo },
    { name: "Asana", logo: asanaLogo },
    { name: "Evernote", logo: evernoteLogo },
    { name: "Zapier", logo: zapierLogo },
    { name: "HubSpot", logo: hubspotLogo },
  ];

  const plans = [
    // {
    //   name: "Boxia Personal",
    //   monthlyPrice: "Free",
    //   annualPrice: "Free",
    //   period: "",
    //   billing: "",
    //   note: "",
    //   features: [
    //     "Basic productivity tools (Calendar, Notes, To-Do, Files)",
    //     "Single-user account",
    //     "25GB storage",
    //     "Community support",
    //     "Basic AI Copilot",
    //     "Basic third-party integrations (up to 5)",
    //   ],
    //   cta: "Get Started Free",
    // },
    {
      name: "Boxia Pro",
      monthlyPrice: "$12.99",
      annualPrice: "$9.99",
      period: "/month",
      billing: annualBilling ? "billed annually" : "billed monthly",
      features: [
        "Everything in Personal plus:",
        "Advanced productivity tools (Docs, Sheets, Slides)",
        "Priority support",
        "Unlimited storage",
        "Full Kairo access",
        "Unlimited advanced integrations",
      ],
      cta: "Start 14-day free trial",
      highlight: true,
    },
    {
      name: "Boxia Team",
      monthlyPrice: "$12.99",
      annualPrice: "$12.99",
      period: "/user/month",
      billing: annualBilling ? "billed annually" : "billed monthly",
      note: "Minimum 3 users",
      features: [
        "Everything in Pro plus:",
        "Shared workspaces and collaborative tools",
        "Team analytics and insights",
        "Admin controls and role management",
        "Enhanced security features",
        "Unlimited shared storage",
      ],
      cta: "Start 14-day free trial",
    },
    {
      name: "Boxia Enterprise",
      price: "Custom",
      features: [
        "Everything in Team plus:",
        "Custom integrations and workflows",
        "On-premises deployment option",
        "Advanced security (SSO, audit logs, custom permissions)",
        "Dedicated account manager",
        "Custom onboarding and training",
      ],
      cta: "Contact Sales",
    },
  ];

  // Structured Data (SoftwareApplication) for SEO Rich Results
  const softwareLD = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Boxia",
    applicationCategory: "ProductivityApplication",
    operatingSystem: "Web",
    url: "https://boxia.io/",
    description:
      "Boxia is an all-in-one productivity platform providing unified task management, calendar scheduling, notes, docs, files, AI assistants, time tracking and a habit tracker—helping teams stay organized, save time and increase productivity long term.",
    featureList: [
      "Calendar & task management",
      "Notes & documentation",
      "Unified files & storage",
      "AI productivity copilot (Kairo & Tab)",
      "Time tracking & habit insights",
    ],
    keywords:
      "all-in-one productivity platform, productivity, task management, time tracking, habit tracker, productivity tools, unified workspace",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      ratingCount: "137",
    },
    offers: {
      "@type": "Offer",
      price: "9.99",
      priceCurrency: "USD",
    },
    publisher: {
      "@type": "Organization",
      name: "Boxia",
      url: "https://boxia.io/",
      logo: {
        "@type": "ImageObject",
        url: "https://boxia.io/logo192.png",
      },
    },
  };

  // Polished Tab (autocomplete) demo component
  const TabDemoEditor = () => {
    // Minimal state machine: typing -> suggestion visible -> accepted
    const fullText = "Sprint planning meeting notes";
    const suggestion = " – finalize authentication flow";
    const [typed, setTyped] = React.useState("");
    const [phase, setPhase] = React.useState("typing"); // typing | suggest | accepted
    React.useEffect(() => {
      if (phase === "typing") {
        if (typed.length < fullText.length) {
          const t = setTimeout(
            () => setTyped(fullText.slice(0, typed.length + 1)),
            55
          );
          return () => clearTimeout(t);
        } else {
          const t = setTimeout(() => setPhase("suggest"), 400);
          return () => clearTimeout(t);
        }
      }
      if (phase === "suggest") {
        const t = setTimeout(() => setPhase("accepted"), 1100); // auto-press Tab
        return () => clearTimeout(t);
      }
      if (phase === "accepted") {
        const t = setTimeout(() => {
          setTyped("");
          setPhase("typing");
        }, 3000); // restart after 3s pause
        return () => clearTimeout(t);
      }
    }, [typed, phase]);
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 font-geiro text-sm p-5 min-h-[220px] flex flex-col">
        <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
          Note
        </div>
        <div className="relative flex-1">
          <span className="text-gray-900 dark:text-gray-100">{typed}</span>
          {phase === "suggest" && (
            <span className="text-gray-400 dark:text-gray-600 select-none">
              {suggestion}
              <span className="ml-2 rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-[10px] text-gray-600 dark:text-gray-300">
                Tab
              </span>
            </span>
          )}
          {phase === "accepted" && (
            <span className="text-blue-600 dark:text-blue-300 transition-colors">
              {suggestion}
            </span>
          )}
          <span className="inline-block w-0.5 h-4 bg-blue-500 ml-0.5 animate-pulse align-middle" />
        </div>
        <div className="mt-4 flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500">
          {/* <div className="flex items-center gap-2">
            <Zap className="w-3 h-3" />
            {phase === "accepted" ? "Applied in 0.01s" : phase === "suggest" ? "Suggestion ready" : "Typing"}
          </div> */}
          {phase !== "accepted" && (
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              <span className="rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-[9px]">
                Tab
              </span>
              to accept
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <SEO
        title="Boxia – All-in-One Productivity Platform: Unified Calendar, Tasks, Notes, Files & AI Workspace"
        description="Boxia is an all-in-one productivity platform for high-focus teams—unifying task management, calendar, notes, docs, files, AI assistants, time tracking and habit tracker tools to increase productivity and save time."
        url={
          typeof window !== "undefined"
            ? window.location.origin
            : "https://boxia.io/"
        }
        image={"https://boxia.io/social-preview.png"}
        type="website"
        tags={[
          "productivity",
          "all-in-one productivity platform",
          "task management",
          "calendar",
          "notes",
          "time tracking",
          "habit tracker",
          "AI",
          "workspace",
          "Boxia",
          "productivity tools",
        ]}
        structuredData={softwareLD}
      />
      <main
        className={`flex-grow overflow-y-auto transition-colors duration-300 ${
          darkMode ? "dark bg-[#1a1a1a] text-white" : "bg-white text-gray-800"
        }`}
      >
        <NavBar darkMode={darkMode} logoFade={logoFade} />

        <HeroSection
          title="The All‑in‑One Productivity Platform"
          subtitle={{
            regular: "Transform your workflow with ",
            gradient: "seamlessly connected tools.",
          }}
          description="Work, collaborate, and create without limits—Notes, Tasks, Docs, Whiteboards, and more, all in one place. Powered by AI. Built for you."
          ctaText="Sign Up Now"
          ctaHref="https://app.boxia.io/auth/signup"
          bottomImage={{
            light: BoxiaHome,
            dark: BoxiaHome,
          }}
          gridOptions={{
            angle: 60,
            opacity: 0.25,
            cellSize: 40,
            lightLineColor: "#4a4a4a",
            darkLineColor: "#2a2a2a",
          }}
          engineerLogos={[
            { name: "OpenAI", logo: openaiLogo },
            { name: "Microsoft", logo: microsoftBig },
            { name: "Google", logo: googleBig },
            { name: "Amazon", logo: amazonLogo },
            { name: "Meta", logo: metaLogo },
            { name: "GitHub", logo: githubBig },
          ]}
        />

        {/* Key Features Showcase */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Features
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Your All-in-One{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Productivity Suite
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Transform your workflow with our comprehensive set of tools
                designed to boost your productivity
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
              {[
                {
                  icon: Calendar,
                  title: "Calendar & To-Do Management",
                  description:
                    "Powerful calendar and task management with seamless Microsoft 365 integration",
                  subFeatures: [
                    "Seamless Microsoft 365 Calendar and Todo Integration",
                    "Multiple Calendar Views (Day, Week, Month, Agenda)",
                    "Real-Time Collaboration with Shared Calendars",
                    "Priority Levels, Tags, and Color Coding",
                  ],
                },
                {
                  icon: FileText,
                  title: "Notes & Documentation",
                  description:
                    "Rich text notes with real-time collaboration and smart organization",
                  subFeatures: [
                    "Rich Text Notes with Real-Time Collaboration",
                    "Cross-Note Linking and Smart Suggestions",
                    "Auto-Saving and Version History",
                    "Password Protection and Encryption",
                  ],
                },
                {
                  icon: Users,
                  title: "Spaces",
                  description:
                    "Collaborative documents and wikis with real-time editing",
                  subFeatures: [
                    "Real-Time Collaborative Editing",
                    "Block-Based Editor with Rich Embeds",
                    "Project and Team Workspaces",
                    "Role-Based Access Control",
                  ],
                },
                {
                  icon: Pencil,
                  title: "Whiteboard",
                  description:
                    "Visual collaboration with infinite canvas and real-time editing",
                  subFeatures: [
                    "Infinite Canvas with Drawing Tools",
                    "Real-Time Collaboration",
                    "Shapes and Asset Uploading",
                    "Auto-Saving and Version Control",
                  ],
                },
                {
                  icon: FolderOpen,
                  title: "File Management",
                  description:
                    "Centralized storage with cloud integration and version control",
                  subFeatures: [
                    "Google Drive and OneDrive Integration",
                    "Advanced Search and Filtering",
                    "Version Control and History",
                    "Secure Role-Based Sharing",
                  ],
                },
                {
                  icon: MessageSquare,
                  title: "Unified Messaging",
                  description:
                    "All-in-one inbox for seamless communication (Coming Soon)",
                  subFeatures: [
                    "All-in-One Inbox for Email and Chats",
                    "Integrated Message Threads",
                    "Real-Time Messaging",
                    "Smart Filters and Tagging",
                  ],
                },
                {
                  icon: Bot,
                  title: "AI-Powered Productivity",
                  description: "Smart automation and insights powered by AI",
                  subFeatures: [
                    "Boxia Copilot for Task Management",
                    "AI-Generated Summaries",
                    "Task Automation",
                    "Smart Recommendations",
                  ],
                },
                {
                  icon: Shield,
                  title: "Security & Privacy",
                  description:
                    "Enterprise-grade security with end-to-end encryption",
                  subFeatures: [
                    "End-to-End Encryption",
                    "Role-Based Access Control",
                    "Audit Logs and History",
                    "Multi-Factor Authentication",
                  ],
                },
                {
                  icon: Cog,
                  title: "Integrations & Automation",
                  description: "Seamless integration with your favorite tools",
                  subFeatures: [
                    "Google Workspace Integration",
                    "Microsoft 365 Integration",
                    "Custom Webhooks",
                    "API Access (Coming Soon)",
                  ],
                },
                // {
                //   icon: Layout,
                //   title: "Modern UI/UX",
                //   description: "Fast, responsive, and beautiful interface",
                //   subFeatures: [
                //     "Intuitive Modern Design",
                //     "Real-Time Collaboration",
                //     "Cross-Platform Support",
                //     "Customizable Dashboard"
                //   ]
                // }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative space-y-4">
                    <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                      <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {feature.title}
                    </h3>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>

                    <ul className="space-y-2">
                      {feature.subFeatures.map((subFeature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                        >
                          <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                          {subFeature}
                        </li>
                      ))}
                    </ul>

                    <a
                      href="/features"
                      className="inline-flex items-center text-sm font-medium text-purple-600 dark:text-purple-300 group-hover:text-pink-500 dark:group-hover:text-orange-200 transition-colors duration-300"
                    >
                      Learn more
                      <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 duration-300" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Boxia Kairo Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Boxia Kairo
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Meet{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Kairo
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Your AI workspace assistant that actually gets work done. Ask
                Kairo to schedule meetings, organize tasks, write emails, or
                create content, and watch it happen across your entire workspace
                instantly.
              </p>
            </div>

            <div className="flex flex-col justify-center space-y-10 mt-16">
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed text-center max-w-4xl mx-auto">
                Kairo brings everything together. Start a conversation and end
                with scheduled meetings, updated tasks, triaged emails, and
                organized notes. All without leaving your workspace or switching
                between apps.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/40 border border-purple-200 dark:border-purple-800">
                    <SparkleIcon className="w-6 h-6 text-purple-600 dark:text-purple-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Truly Intelligent
                    </h4>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      Kairo doesn't just chat. It plans, executes, and follows
                      through on complex work across your entire workspace.
                    </p>
                    <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <li>Understands your full workspace</li>
                      <li>Handles multi-step projects</li>
                      <li>Learns your workflow patterns</li>
                      <li>Works autonomously when needed</li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/40 border border-purple-200 dark:border-purple-800">
                    <Link2 className="w-6 h-6 text-purple-600 dark:text-purple-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Deeply Integrated
                    </h4>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      One conversation can update tasks, schedule meetings, send
                      emails, organize notes, and manage your boards. No
                      switching, no copying.
                    </p>
                    <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <li>Manages tasks and boards</li>
                      <li>Schedules and updates calendar</li>
                      <li>Drafts and sends emails</li>
                      <li>Organizes files and notes</li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/40 border border-purple-200 dark:border-purple-800">
                    <SlidersHorizontal className="w-6 h-6 text-purple-600 dark:text-purple-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Your Choice of AI
                    </h4>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      Choose the AI model that fits your task. Compare different
                      models side by side, or let Kairo pick the best one for
                      you.
                    </p>
                    <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <li>Access to leading AI models</li>
                      <li>Switch models anytime</li>
                      <li>Compare responses instantly</li>
                      <li>Adjust depth and speed</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Boxia Tab Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-blue-950/10 dark:bg-blue-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(59,130,246,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(59,130,246,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Boxia Tab
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Tab Tab Tab at{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-300 dark:to-cyan-300">
                  Light Speed
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                The world's fastest autocomplete and prediction model, built
                directly into Boxia. Experience unprecedented workflow velocity
                with intelligent predictions
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mt-16">
              {/* Left side - Tab Info */}
              <div className="flex flex-col justify-center space-y-10 order-2 lg:order-1">
                <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                  Imagine completing your thoughts before you finish typing. Tab
                  learns your writing patterns and project context to predict
                  exactly what you want to say next, saving you hours.
                </p>

                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800">
                      <Zap className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Instant Completions
                      </h4>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        With sub-20ms response time, suggestions appear faster
                        than you can blink.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800">
                      <Brain className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Context-Aware
                      </h4>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        Understands your project, team, and writing style to
                        provide relevant predictions.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800">
                      <FileEdit className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Works Everywhere
                      </h4>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        Accelerates your workflow in notes, docs, emails, chat,
                        and beyond.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Tab Interface (refined) */}
              <div className="relative order-1 lg:order-2">
                <TabDemoEditor />
              </div>
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Integrations
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Connect Your{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Favorite Tools
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Seamlessly integrate with the tools you already use to create a
                unified workspace
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
              {[
                {
                  icon: CalendarDays,
                  title: "Calendar & Task Integrations",
                  description: "Sync your calendars and tasks across platforms",
                  subFeatures: [
                    "Microsoft 365 Calendar & To-Do Integration",
                    "Google Calendar & Tasks Sync",
                    "Slack Calendar Integration",
                    "AI-Powered Event Parsing",
                  ],
                },
                {
                  icon: FolderGit2,
                  title: "File Storage & Documents",
                  description: "Access your files from any cloud storage",
                  subFeatures: [
                    "Google Drive Integration",
                    "Microsoft OneDrive Sync",
                    "Dropbox Connection",
                    "GitHub Repository Access",
                  ],
                },
                {
                  icon: MessagesSquare,
                  title: "Messaging & Collaboration",
                  description: "Connect your communication tools",
                  subFeatures: [
                    "Slack Integration (Coming Soon)",
                    "Microsoft Teams Connection",
                    "Zoom & Google Meet Integration",
                    "Email to Task Conversion",
                  ],
                },
                {
                  icon: Workflow,
                  title: "Automation & Workflows",
                  description: "Build powerful automated workflows",
                  subFeatures: [
                    "Pipedream Integration",
                    "Zapier Connection",
                    "Custom Webhooks Support",
                    "No-Code Automation Builder",
                  ],
                },
                {
                  icon: KeyRound,
                  title: "Security & Authentication",
                  description: "Enterprise-grade security integrations",
                  subFeatures: [
                    "Okta SSO Support",
                    "Google Workspace Login",
                    "Microsoft SSO",
                    "Two-Factor Authentication (2FA)",
                  ],
                },
                {
                  icon: Code2,
                  title: "Developer Tools",
                  description: "Build custom integrations and extensions",
                  subFeatures: [
                    "Boxia API Access",
                    "Custom Webhooks",
                    "GitHub Actions Integration",
                    "API Documentation",
                  ],
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative space-y-4">
                    <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                      <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {feature.title}
                    </h3>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>

                    <ul className="space-y-2">
                      {feature.subFeatures.map((subFeature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                        >
                          <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                          {subFeature}
                        </li>
                      ))}
                    </ul>

                    <a
                      href="/features"
                      className="inline-flex items-center text-sm font-medium text-purple-600 dark:text-purple-300 group-hover:text-pink-500 dark:group-hover:text-orange-200 transition-colors duration-300"
                    >
                      Learn more
                      <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 duration-300" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customization Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Customization
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Make Boxia{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Truly Yours
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Customize every aspect of your workspace to match your unique
                workflow and preferences
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
              {[
                {
                  icon: Palette,
                  title: "Personalized User Interface",
                  description: "Create your perfect workspace environment",
                  subFeatures: [
                    "Customizable Sidebar & Layout",
                    "Dark Mode & Theme Customization",
                    "Responsive UI with Animations",
                    "Custom Icons & Visual Elements",
                  ],
                },
                {
                  icon: LayoutDashboard,
                  title: "Dashboard & Workspace",
                  description: "Build custom views for teams and projects",
                  subFeatures: [
                    "Personalized Dashboards",
                    "Block-Based Editor (Spaces)",
                    "Custom Widgets & Sections",
                    "Role-Based Workspaces",
                  ],
                },
                {
                  icon: KanbanSquare,
                  title: "Task & Calendar Views",
                  description: "Organize work your way with flexible views",
                  subFeatures: [
                    "Custom Task Views & Layouts",
                    "Configurable Fields & Columns",
                    "Multiple Calendar Views",
                    "Drag-and-Drop Organization",
                  ],
                },
                {
                  icon: Bell,
                  title: "Messaging & Notifications",
                  description: "Control your communication preferences",
                  subFeatures: [
                    "Custom Notification Rules",
                    "Smart Mentions & Alerts",
                    "AI-Powered Filters",
                    "Priority Message Settings",
                  ],
                },
                {
                  icon: Settings2,
                  title: "AI Personalization",
                  description: "Train AI to work the way you do",
                  subFeatures: [
                    "AI Agent Customization",
                    "Copilot Command Shortcuts",
                    "Custom Automations",
                    "AI Style Preferences",
                  ],
                },
                {
                  icon: Lock,
                  title: "Security & Access",
                  description: "Configure security to match your needs",
                  subFeatures: [
                    "Granular Access Controls",
                    "Custom Roles & Permissions",
                    "Password Protection",
                    "Audit Log Settings",
                  ],
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative space-y-4">
                    <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                      <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {feature.title}
                    </h3>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>

                    <ul className="space-y-2">
                      {feature.subFeatures.map((subFeature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                        >
                          <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                          {subFeature}
                        </li>
                      ))}
                    </ul>

                    <a
                      href="/features"
                      className="inline-flex items-center text-sm font-medium text-purple-600 dark:text-purple-300 group-hover:text-pink-500 dark:group-hover:text-orange-200 transition-colors duration-300"
                    >
                      Learn more
                      <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 duration-300" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Security & Privacy Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Security & Privacy
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Your Data,{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Your Control
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Enterprise-grade security and privacy features to keep your
                information safe and compliant
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
              {[
                {
                  icon: ShieldCheck,
                  title: "End-to-End Encryption",
                  description: "Military-grade encryption for all your data",
                  subFeatures: [
                    "AES-256 bit encryption at rest",
                    "TLS 1.3 for data in transit",
                    "Zero-knowledge architecture",
                    "Encrypted file storage",
                  ],
                },
                {
                  icon: Key,
                  title: "Access Control",
                  description: "Granular permissions and role management",
                  subFeatures: [
                    "Role-based access control (RBAC)",
                    "Custom permission sets",
                    "Team and project-level security",
                    "Access audit logging",
                  ],
                },
                {
                  icon: Fingerprint,
                  title: "Authentication",
                  description: "Multi-factor security for your accounts",
                  subFeatures: [
                    "Multi-factor authentication (MFA)",
                    "Single sign-on (SSO)",
                    "Biometric authentication",
                    "Session management",
                  ],
                },
                {
                  icon: Shield,
                  title: "Compliance",
                  description: "Meet industry security standards",
                  subFeatures: [
                    "GDPR compliance",
                    "HIPAA compliance ready",
                    "SOC 2 Type II certified",
                    "Regular security audits",
                  ],
                },
                {
                  icon: History,
                  title: "Data Control",
                  description: "Complete control over your information",
                  subFeatures: [
                    "Data export capabilities",
                    "Version history",
                    "Backup and recovery",
                    "Data retention policies",
                  ],
                },
                {
                  icon: ServerCrash,
                  title: "Infrastructure Security",
                  description: "Secure and reliable platform foundation",
                  subFeatures: [
                    "DDoS protection",
                    "24/7 monitoring",
                    "Automated threat detection",
                    "Regular penetration testing",
                  ],
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative space-y-4">
                    <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                      <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {feature.title}
                    </h3>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>

                    <ul className="space-y-2">
                      {feature.subFeatures.map((subFeature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                        >
                          <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                          {subFeature}
                        </li>
                      ))}
                    </ul>

                    <a
                      href="/features"
                      className="inline-flex items-center text-sm font-medium text-purple-600 dark:text-purple-300 group-hover:text-pink-500 dark:group-hover:text-orange-200 transition-colors duration-300"
                    >
                      Learn more
                      <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 duration-300" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section - Price Display Fix */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Pricing
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Simple Pricing for{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Powerful Productivity
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Choose the plan that fits your needs and start boosting
                productivity. ⭐️{" "}
                <span className="font-medium">4.9/5 average rating</span> (137
                early access reviews) – teams save time and increase
                productivity with unified task & project workflows.
              </p>
            </div>

            <div className="flex justify-center mt-10">
              <div className="inline-flex items-center rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-1">
                <button
                  className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    annualBilling
                      ? "bg-gradient-to-tr from-purple-600/80 to-pink-500/80 dark:from-purple-300/80 dark:to-orange-200/80 text-white shadow-lg"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
                  onClick={() => setAnnualBilling(true)}
                >
                  Annual Billing
                </button>
                <button
                  className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    !annualBilling
                      ? "bg-gradient-to-tr from-purple-600/80 to-pink-500/80 dark:from-purple-300/80 dark:to-orange-200/80 text-white shadow-lg"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
                  onClick={() => setAnnualBilling(false)}
                >
                  Monthly Billing
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`group relative overflow-hidden rounded-xl border-[1px] ${
                    plan.name === "Pro"
                      ? "border-purple-500/20 dark:border-purple-300/20"
                      : "border-black/5 dark:border-white/5"
                  } bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl`}
                >
                  {plan.name === "Pro" && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 px-3 py-1 text-xs font-medium text-white rounded-bl-lg rounded-tr-xl shadow-lg">
                      Popular
                    </div>
                  )}

                  <div className="relative space-y-4 h-full flex flex-col">
                    <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {plan.name}
                    </h3>

                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                        {plan.name === "Enterprise"
                          ? ""
                          : plan.name === "Team"
                          ? (annualBilling
                              ? plan.annualPrice
                              : plan.monthlyPrice) * teamUsers
                          : annualBilling
                          ? plan.annualPrice
                          : plan.monthlyPrice}
                      </span>
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                        {plan.name === "Enterprise" ? "" : "/month"}
                      </span>
                    </div>

                    {plan.name === "Team" && (
                      <div className="mt-2 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Number of users: {teamUsers}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            $
                            {(annualBilling
                              ? plan.annualPrice
                              : plan.monthlyPrice) * teamUsers}
                            /mo
                          </span>
                        </div>
                        <input
                          type="range"
                          min="3"
                          max="50"
                          value={teamUsers}
                          onChange={(e) =>
                            setTeamUsers(parseInt(e.target.value))
                          }
                          className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-purple-600/20 to-pink-500/20 dark:from-purple-300/20 dark:to-orange-200/20"
                        />
                        <style jsx>{`
                          input[type="range"]::-webkit-slider-thumb {
                            -webkit-appearance: none;
                            appearance: none;
                            width: 16px;
                            height: 16px;
                            border-radius: 50%;
                            background: linear-gradient(
                              to right,
                              #9333ea,
                              #ec4899
                            );
                            cursor: pointer;
                          }
                          input[type="range"]::-moz-range-thumb {
                            width: 16px;
                            height: 16px;
                            border-radius: 50%;
                            background: linear-gradient(
                              to right,
                              #9333ea,
                              #ec4899
                            );
                            cursor: pointer;
                            border: none;
                          }
                        `}</style>
                      </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {plan.name === "Team" ? "Starting from 3 users" : ""}
                    </p>

                    <ul className="space-y-3 pt-4 flex-grow">
                      {plan.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                        >
                          <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      className={`w-full mt-6 px-6 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                        plan.name === "Pro"
                          ? "bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl"
                          : "bg-gradient-to-tr from-zinc-300/30 via-gray-400/30 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 text-gray-900 dark:text-gray-100 hover:bg-gradient-to-tr hover:from-zinc-300/40 hover:via-gray-400/40"
                      }`}
                    >
                      {plan.name === "Enterprise"
                        ? "Contact Us"
                        : "Get Started"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                FAQ
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Got{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Questions?
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Find answers to common questions about Boxia
              </p>
            </div>

            <div className="max-w-3xl mx-auto mt-16 space-y-4">
              {[
                {
                  question: "Is there a free plan available?",
                  answer:
                    "We don't currently offer a free plan, but we're working on one! For now, we offer a 14-day free trial for all our paid plans. After the trial, you'll be automatically charged for the plan you selected. If you cancel your subscription, you won't be able to access Boxia until you resubscribe.",
                },
                {
                  question: "Is my data secure with Boxia?",
                  answer:
                    "Absolutely. We use bank-level encryption and adhere to strict privacy standards to ensure your data is always protected. All data is encrypted both in transit and at rest using industry-standard protocols.",
                },
                {
                  question: "Can I cancel my subscription anytime?",
                  answer:
                    "Yes, you can cancel your subscription at any time with no questions asked. However, if you cancel your subscription, you won't be able to access Boxia until you resubscribe. Any unused portion of paid subscriptions will be refunded on a prorated basis.",
                },
                {
                  question: "How does the 14-day free trial work?",
                  answer:
                    "You can use all Boxia features for 14 days without any charge. No credit card is required to start your trial. At the end of the trial, you'll be automatically charged for the plan you selected. If you cancel your subscription, you won't be able to access Boxia until you resubscribe.",
                },
                {
                  question: "What kind of support do you offer?",
                  answer:
                    "We offer 24/7 email support for all users. Pro and Team plan subscribers also get priority support with faster response times and access to video chat support. Enterprise customers receive dedicated support channels and custom SLAs.",
                },
                {
                  question: "Can I import data from other tools?",
                  answer:
                    "Yes, Boxia supports importing data from various popular tools including Microsoft 365, Google Workspace, Notion, and more. Our import tools help maintain your existing structure and relationships between items.",
                },
                {
                  question: "Do you offer custom enterprise solutions?",
                  answer:
                    "Yes, we provide customized enterprise solutions with dedicated support, custom integrations, and specific security requirements. Contact our sales team to discuss your organization's needs.",
                },
                {
                  question:
                    "How does Boxia help with measuring productivity and long-term growth?",
                  answer:
                    "Boxia provides time tracking and task management insights—showing tasks completed per hour worked, trending productivity growth across projects, and habit tracker patterns. While macro metrics like gross domestic product (GDP), national productivity, total factor productivity, goods and services output, economic growth and standard of living apply at an economic scale (e.g., the United States), Boxia focuses on practical team and project-level productivity tools that save time and drive continuous long-term improvement.",
                },
                {
                  question:
                    "Can Boxia replace multiple tools and assist a project manager?",
                  answer:
                    "Yes. As an all-in-one productivity platform, Boxia consolidates task and project planning, calendar & scheduling, notes & documentation, files, AI assistants, time tracking and habit tracker features. This unified workspace acts like a smart assistant for a project manager—helping teams stay organized, increase productivity, and save time without juggling disconnected apps.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 transition-all duration-300 hover:shadow-xl"
                >
                  <button
                    onClick={() => {
                      const newOpenStates = [...openStates];
                      newOpenStates[index] = !newOpenStates[index];
                      setOpenStates(newOpenStates);
                    }}
                    className="flex w-full items-center justify-between p-6 text-left"
                  >
                    <span className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {item.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                        openStates[index] ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openStates[index] ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <p className="px-6 pb-6 text-gray-600 dark:text-gray-400">
                      {item.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Alpha Access
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Be Part of the Future of{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Work
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Join the Boxia Alpha and be among the first to experience
                Boxia's revolutionary productivity suite
              </p>

              <div className="mt-10 flex flex-col items-center gap-4">
                <a
                  href="https://app.boxia.io/auth/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Sign Up Now
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 duration-300" />
                </a>
                <span className="text-sm text-gray-500 dark:text-gray-400"></span>
              </div>
            </div>

            <div className="mt-20 space-y-10">
              <div className="relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8">
                <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-8 text-center">
                  Trusted by Industry Leaders
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center justify-center">
                  {[
                    { name: "google", logo: googleLogo },
                    { name: "microsoft", logo: microsoftLogo },
                    { name: "slack", logo: slackLogo },
                    { name: "dropbox", logo: dropboxLogo },
                    { name: "github", logo: githubLogo },
                    { name: "salesforce", logo: salesforceLogo },
                  ].map((company) => (
                    <div
                      key={company.name}
                      className="flex items-center justify-center group"
                    >
                      <img
                        src={company.logo}
                        alt={`${company.name} logo`}
                        className="w-auto h-8 opacity-50 group-hover:opacity-100 transition-opacity duration-300 dark:invert dark:brightness-0 dark:contrast-200"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: Zap,
                    title: "Lightning Fast",
                    description:
                      "Experience instant response times and real-time collaboration",
                  },
                  {
                    icon: Shield,
                    title: "Enterprise Ready",
                    description:
                      "Bank-level security with SOC 2 Type II certification",
                  },
                  {
                    icon: Sparkles,
                    title: "AI Powered",
                    description:
                      "Advanced AI features to supercharge your productivity",
                  },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 text-center"
                  >
                    <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3 mb-4">
                      <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Footer darkMode={darkMode} />
      </main>
    </div>
  );
};

export default LandingPage;
